import { NurseLayout } from "@/components/layout/nurse-layout";
import { useListGuidelines, useGetGuidelinesSummary } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle } from "lucide-react";
import { useMemo } from "react";

function isStale(dateStr: string): boolean {
  const updated = new Date(dateStr);
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  return updated < twelveMonthsAgo;
}
import { useSearchParam } from "@/hooks/use-search-param";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import { Highlight } from "@/components/ui/highlight";

export default function NurseGuidelines() {
  useScrollRestore("nurse-guidelines");
  const [category, setCategory] = useSearchParam("category", "", { storageKey: "nurse-guidelines:category" });
  const [search, setSearch] = useSearchParam("q", "", { storageKey: "nurse-guidelines:search" });
  const { data: guidelines, isLoading } = useListGuidelines({ category: category || undefined });
  const { data: summary } = useGetGuidelinesSummary();

  const filtered = useMemo(() => {
    if (!guidelines) return [];
    const q = search.trim().toLowerCase();
    if (!q) return guidelines;
    return guidelines.filter(
      g =>
        g.title.toLowerCase().includes(q) ||
        g.summary?.toLowerCase().includes(q) ||
        g.organisation?.toLowerCase().includes(q),
    );
  }, [guidelines, search]);

  const resultCount = filtered.length;

  if (isLoading) return <NurseLayout title="Clinical Guidelines"><LoadingScreen /></NurseLayout>;

  return (
    <NurseLayout title="Clinical Guidelines">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <p className="text-slate-600">Browse official guidelines from NICE, BSR, EULAR and others.</p>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search guidelines…"
              className="pl-9"
            />
          </div>
          <div className="w-52">
            <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {summary?.byCategory.map(c => (
                  <SelectItem key={c.category} value={c.category}>{c.category} ({c.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <p className={`mb-4 text-sm ${resultCount === 0 ? "text-rose-500" : "text-slate-500"}`}>
        {resultCount === 0 ? "No guidelines found" : `Showing ${resultCount} ${resultCount === 1 ? "guideline" : "guidelines"}`}
      </p>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map(g => (
          <Link key={g.id} href={`/nurse/guidelines/${g.id}`}>
            <Card className="h-full hover:border-teal-500 hover:shadow-md transition-all cursor-pointer flex flex-col">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <Badge variant="outline" className="bg-slate-50 text-slate-700">{g.organisation}</Badge>
                  <Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-50">{g.category}</Badge>
                </div>
                <CardTitle className="text-lg leading-tight group-hover:text-teal-700">
                  <Highlight text={g.title} query={search} />
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between">
                <p className="text-sm text-slate-600 line-clamp-3 mb-4">
                  <Highlight text={g.summary ?? ""} query={search} />
                </p>
                <div className="mt-auto space-y-2">
                  {g.updatedAt && (
                    <div className={`flex flex-col gap-1 text-xs ${isStale(g.updatedAt) ? "text-amber-600" : "text-slate-400"}`}>
                      <div className="flex items-center gap-1.5">
                        {isStale(g.updatedAt) && <AlertTriangle className="h-3 w-3 shrink-0" />}
                        Last verified: {new Date(g.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                      {isStale(g.updatedAt) && (
                        <p className="text-amber-600 leading-snug">Content may be outdated — verify against original source before use.</p>
                      )}
                    </div>
                  )}
                  <div className="flex items-center text-sm font-medium text-teal-600">
                    View details
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full py-12 text-center text-slate-500 bg-white rounded-lg border border-slate-200">
            No guidelines found{search ? ` matching "${search}"` : " for the selected filter"}.
          </div>
        )}
      </div>
    </NurseLayout>
  );
}
